import React, { useState } from "react";
import {
  Grid,
  Box,
  Typography,
  IconButton,
  Paper,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import MoreVertIcon from "@mui/icons-material/MoreVert";

const FolderGrid = ({ folders, onOpen, onDelete, onRename, currentPath }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");

  const openMenu = (e, folder) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedFolder(folder);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const startRename = () => {
    setNewFolderName(selectedFolder || "");
    closeMenu();
    setTimeout(() => setRenameDialogOpen(true), 50);
  };

  const handleRename = () => {
    if (newFolderName.trim() && selectedFolder) {
      onRename(selectedFolder, newFolderName.trim());
    }
    setRenameDialogOpen(false);
    setSelectedFolder(null);
    setNewFolderName("");
  };

  const handleDelete = () => {
    if (selectedFolder) {
      onDelete(selectedFolder);
    }
    closeMenu();
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.96)",
    backdropFilter: "blur(10px)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
    overflow: "hidden",
  };

  const fieldSx = {
    mt: 1.25,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
  };

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "#223142",
            mb: 0.5,
          }}
        >
          Folders
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "#6b778c",
          }}
        >
          Browse and organize folders in the current location.
        </Typography>
      </Box>

      <Grid container spacing={2.5}>
        {folders.map((folder) => (
          <Grid item key={folder} xs={12} sm={6} md={4} lg={3} xl={2}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: "16px",
                transition: "all 0.2s ease",
                cursor: "pointer",
                position: "relative",
                border: "1px solid rgba(47,99,173,0.08)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                backgroundColor: "rgba(255,255,255,0.96)",
                height: "100%",
                minHeight: 150,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                "&:hover": {
                  boxShadow: "0 12px 28px rgba(0,0,0,0.1)",
                  transform: "translateY(-2px)",
                  borderColor: "rgba(47,99,173,0.18)",
                },
              }}
              onClick={() => onOpen(`${currentPath}/${folder}`)}
            >
              <Tooltip title="Options">
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    zIndex: 1,
                    borderRadius: "10px",
                    backgroundColor: "rgba(0,0,0,0.03)",
                  }}
                  onClick={(e) => openMenu(e, folder)}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mb: 1.5,
                }}
              >
                <FolderIcon sx={{ fontSize: 58, color: "#eab308" }} />
              </Box>

              <Typography
                variant="body2"
                align="center"
                sx={{
                  fontWeight: 600,
                  color: "#223142",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
              >
                {folder}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 14px 30px rgba(0,0,0,0.14)",
            border: "1px solid rgba(47,99,173,0.08)",
          },
        }}
      >
        <MenuItem onClick={startRename}>Rename</MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "#d32f2f" }}>
          Delete
        </MenuItem>
      </Menu>

      <Dialog
        open={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.3rem",
            pt: 3,
            pb: 1,
            color: "#1f1f1f",
          }}
        >
          Rename Folder
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography
            sx={{
              mt: 0.5,
              mb: 1,
              textAlign: "center",
              color: "#666",
              fontSize: "0.95rem",
            }}
          >
            Update the folder name below.
          </Typography>

          <TextField
            autoFocus
            fullWidth
            label="Folder Name"
            type="text"
            variant="outlined"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            sx={fieldSx}
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1.5,
            justifyContent: "center",
            gap: 1.25,
          }}
        >
          <Button
            onClick={() => setRenameDialogOpen(false)}
            variant="outlined"
            sx={secondaryButtonSx}
          >
            Cancel
          </Button>
          <Button onClick={handleRename} variant="contained" sx={primaryButtonSx}>
            Rename
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FolderGrid;
